import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Modal, ModalContent, ModalPortal } from 'components/common/modal';
import { ReaderBlockEmbedIframeContent } from '.';

import type { LocaleMessages, Theme } from 'shared-types';

interface Props {
  blockId: number;
  data: any;
  fontSize: 'small' | 'medium' | 'large' | 'huge';
  iframeKey: string;
  isScorm: boolean;
  messages: LocaleMessages;
  setIframeWidth?: (width: number) => void;
  showHeader: boolean;
  src: string;
  theme: Pick<Theme, 'textColorLight' | 'textColorLighter'>;
}

const ReaderBlockEmbedIframe = ({
  blockId,
  data,
  fontSize,
  iframeKey,
  isScorm,
  messages,
  setIframeWidth,
  showHeader,
  src,
  theme
}: Props) => {
  const router = useRouter();
  const [actualWidth, setActualWidth] = useState(650);
  const [displayInModal, setDisplayInModal] = useState(false);

  useEffect(() => {
    setWidth();
    router?.events?.on('routeChangeStart', handleRouteChange);
    window.addEventListener('resize', setWidth);

    const iframeEl = document.getElementById(iframeKey) as HTMLIFrameElement;
    iframeEl?.contentWindow?.addEventListener('resize', setWidth);

    return () => {
      router?.events?.off('routeChangeStart', handleRouteChange);
      iframeEl?.contentWindow?.removeEventListener('resize', setWidth);
      window.removeEventListener('resize', setWidth);
      document.removeEventListener('fullscreenchange', exitHandler);
      document.removeEventListener('webkitfullscreenchange', exitHandler);
      document.removeEventListener('mozfullscreenchange', exitHandler);
      document.removeEventListener('MSFullscreenChange', exitHandler);
    };
  }, []);

  useEffect(() => setWidth);

  const handleRouteChange = () => (document.body.style.overflow = '');

  const setWidth = () => {
    const iframeEl = document.getElementById(iframeKey);

    if (iframeEl && actualWidth != iframeEl.offsetWidth) {
      setActualWidth(iframeEl.offsetWidth);
      if (setIframeWidth) setIframeWidth(iframeEl.offsetWidth);
    }
  };

  const requestFullScreen = () => {
    const el = document.getElementById(iframeKey);

    let isAppleTouch = window.navigator.maxTouchPoints > 2;
    if (window.navigator.userAgentData?.platform) {
      isAppleTouch =
        isAppleTouch &&
        /iPad|iPhone|MacIntel/.test(window.navigator.userAgentData?.platform);
    } else {
      isAppleTouch =
        isAppleTouch && /iPad|iPhone|MacIntel/.test(window.navigator.platform);
    }

    if (el) {
      if (isAppleTouch) {
        document.body.style.overflow = 'hidden';
        setDisplayInModal(true);
      } else {
        if (el.requestFullscreen) el.requestFullscreen();
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        else if (el.msRequestFullscreen) el.msRequestFullscreen();

        document.addEventListener('fullscreenchange', exitHandler);
        document.addEventListener('webkitfullscreenchange', exitHandler);
        document.addEventListener('mozfullscreenchange', exitHandler);
        document.addEventListener('MSFullscreenChange', exitHandler);
      }
    } else {
      console.error('could not requestFullScreen: element not found!');
    }
  };

  const exitHandler = () => {
    if (
      !document.fullscreenElement &&
      !document.webkitIsFullScreen &&
      !document.mozFullScreen &&
      !document.msFullscreenElement
    ) {
      const el = document.getElementById(iframeKey) as HTMLIFrameElement;

      if (data.enableFullScreen === 'forced') {
        //scrollIntoView has to be called before hiding the element
        el.scrollIntoView();
        el.hidden = true;
      }

      if (el && el.scrollIntoView) el.scrollIntoView();

      document.removeEventListener('fullscreenchange', exitHandler);
      document.removeEventListener('webkitfullscreenchange', exitHandler);
      document.removeEventListener('mozfullscreenchange', exitHandler);
      document.removeEventListener('MSFullscreenChange', exitHandler);
    }
  };

  const exitModal = () => {
    document.body.style.overflow = '';
    setDisplayInModal(false);
  };

  return (
    <div className="embed">
      {!displayInModal ? (
        <ReaderBlockEmbedIframeContent
          actualWidth={actualWidth}
          blockId={blockId}
          data={data}
          fontSize={fontSize}
          handleFullscreen={requestFullScreen}
          iframeKey={iframeKey}
          isScorm={isScorm}
          messages={messages}
          showHeader={showHeader}
          src={src}
          theme={theme}
        />
      ) : (
        <ModalPortal>
          <Modal
            header={false}
            onHide={exitModal}
            style={{
              marginTop: 35,
              minWidth: '100vw',
              minHeight: '100vh'
            }}
            visible={displayInModal}
          >
            <ModalContent>
              <div style={{ padding: 10 }}>
                <ReaderBlockEmbedIframeContent
                  actualWidth={actualWidth}
                  blockId={blockId}
                  data={data}
                  fontSize={fontSize}
                  handleFullscreen={requestFullScreen}
                  handleModal={exitModal}
                  iframeKey={iframeKey}
                  isInModal={true}
                  isScorm={true}
                  messages={messages}
                  showHeader={showHeader}
                  src={src}
                  theme={theme}
                />
              </div>
            </ModalContent>
          </Modal>
        </ModalPortal>
      )}
    </div>
  );
};

export default ReaderBlockEmbedIframe;
